/**
 * Japanese FAQ translations
 * Source of truth match - English structure
 * Last Updated: January 5, 2026 | Version 3.0
 */
export const faq = {
  // Page Meta
  pageTitle: "よくある質問",
  heroSubtitle: "青年未来観測所について知っておくべきすべてのこと",
  heroDescription: "観測所の仕組み、青年未来指数の開発方法、このプラットフォームの使用方法や貢献方法についての包括的な回答。",
  lastUpdated: "最終更新日：2026年1月5日",
  version: "バージョン 3.0",
  availableLanguages: "英語、ポルトガル語、スペイン語、フランス語、日本語で利用可能",

  // Section Navigation
  sectionUnderstanding: "理解する",
  sectionMethodology: "手法",
  sectionData: "データとソース",
  sectionUsing: "観測所の活用",
  sectionFuture: "将来の開発",
  sectionEvolve: "2026年計画",
  sectionCommon: "よくある質問",
  sectionContact: "お問い合わせ",

  // Section 1: Understanding the Observatory
  understandingTitle: "観測所を理解する",
  
  // Q1: What is the Youth Futures Observatory?
  q1Title: "青年未来観測所とは？",
  q1Answer: "青年未来観測所は、JCIの機関記録とグローバルな青年研究からの検証済みデータを組み合わせて、世界中の青年機会をマッピングするエビデンスベースのインテリジェンスプラットフォームです。JCIの国内および地域組織が活動する広範なコンテキストを理解し、リーダーシップ開発の取り組みが最大のインパクトを持つ場所を特定するのに役立つよう設計されています。",
  q1Detail: "観測所は単にJCIメンバーやプログラムを数えるのではなく、根本的な質問を問いかけます：JCIは世界中の若者の機会拡大にどのように貢献しているか？",
  q1Integrates: "これに答えるために以下を統合しています：",
  q1IntegratesJCI: "JCI機関データ — 114カ国のメンバーからの実際のメンバー、プログラム、参加",
  q1IntegratesGlobal: "グローバル青年機会指数 — 同じ114カ国のメンタルヘルス、労働力開発、起業家精神に関する比較データ",
  q1IntegratesStrategic: "戦略文書 — ビジョン2040の目標とJCIの2040年までの成長野心",
  q1Result: "結果は、JCIがどこで拡大すべきか、何を優先すべきか、グローバルな青年機会の推進における成功をどのように測定するかを示す戦略的ツールです。",

  // Q2: Why "Youth Futures"?
  q2Title: "なぜ「青年未来」観測所と呼ばれるのか？",
  q2Answer: "「青年未来」は、未来研究という学術分野の重要な概念を反映しています。未来研究は、決定と行動が可能な未来をどのように形作るかを検証します。この名前は3つの重要なアイデアをエンコードしています：",
  q2Idea1Title: "「未来」の複数形",
  q2Idea1: "若者は単一の予め決められた未来を持っていません。利用可能な機会、リソースへのアクセス、教育の質、メンタルヘルスサポート、起業家エコシステムの強さに応じて、複数の可能な未来があります。",
  q2Idea2Title: "未来研究の視点",
  q2Idea2: "未来研究は、未来は予め決められたものではなく、今日の選択によって形作られると教えています。現在の状況を分析することで、若者が受け継ぐ可能性のある未来をマッピングしています。ギャップを特定することで、JCIがより良い未来を創造するために介入できる場所を特定しています。",
  q2Idea3Title: "データ駆動型意思決定",
  q2Idea3: "これらの未来を理解するには、各地域で現在何が起こっているかについての厳密なデータが必要であり、リーダーが努力が最大のインパクトを持つ場所について情報に基づいた決定を下すことができます。",
  q2Summary: "観測所は、青年の機会が世界的に均等に分配されていないという現実に声を与えます。ヨーロッパの若者はアフリカ・中東の若者とは全く異なる機会の風景に直面しています。",

  // Q3: Why was it created?
  q3Title: "JCI開発協議会がこの観測所を作成した理由は？",
  q3Answer: "JCIの2023-2027年戦略計画の第4戦略目標はデータ駆動型イノベーションです。この目標は次のように述べています：「リーディング組織であるためには、テクノロジーとイノベーションの最前線にいる必要があります。」",
  q3Questions: "観測所は3つの質問に答えるために作成されました：",
  q3Question1: "インパクトをどのように証明するか？ — JCIは、物語だけでなくデータを通じてインパクトを実証するための厳密で監査可能な方法が必要でした。",
  q3Question2: "どこに努力を集中すべきか？ — 114カ国に4,641の地域組織があるJCIには、努力が最大のインパクトを持つ場所についての戦略的インテリジェンスが必要です。",
  q3Question3: "グローバルな優先事項とどのように整合するか？ — JCIの活動をグローバルな青年機会指数に対してマッピングすることで、JCIは国連SDGsとの整合を実証し、戦略的パートナーとしての地位を確立できます。",
  q3Conclusion: "観測所は実践におけるデータ駆動型イノベーションであり、検証済みデータと体系的分析を使用して戦略的決定を導きます。",

  // Characteristics
  characteristics: {
    dataBased: "実データに基づく",
    dataBasedDesc: "検証可能で監査可能なデータのみ",
    transparent: "完全な透明性",
    transparentDesc: "手法を完全に公開",
    collaborative: "協働的",
    collaborativeDesc: "JCIコミュニティ全体からの貢献で構築",
    accessible: "アクセス可能",
    accessibleDesc: "非商用利用は無料でオープン",
    scientific: "科学的",
    scientificDesc: "国際研究基準に準拠",
    inclusive: "包括的",
    inclusiveDesc: "JCIのグローバルな多様性を反映",
  },

  // Section 2: Observatory Methodology
  methodologyTitle: "観測所の手法",

  // Q4: How was the YFI developed?
  q4Title: "青年未来指数（YFI）はどのように開発されたか？",
  q4Answer: "青年未来指数（YFI）はJCIのために作成された新しい発明ではありません。むしろ、青年機会を測定する確立されたグローバルな研究手法の統合と適応です。",
  q4Steps: "開発方法は以下の通りです：",
  q4Step1Title: "ステップ1：既存のフレームワークを特定",
  q4Step1: "研究チームはグローバル機関からの既存の青年機会指数をレビューしました：WHO精神保健アトラス、ILO雇用データベース、UNESCO教育統計、グローバル起業家精神モニター研究。",
  q4Step2Title: "ステップ2：YFI構造を設計",
  q4Step2: "これらの次元は、研究に裏付けられた重み付けを使用して単一の複合指数に統合されました：",
  q4Weight1: "メンタルヘルス：40%（サービスアクセス、有病率、自殺死亡率）",
  q4Weight2: "労働力と教育：30%（若年失業率、NEET率、スキル開発）",
  q4Weight3: "起業家精神：30%（ビジネス環境、資金調達へのアクセス、イノベーション能力）",
  q4WeightRationale: "重み付けは、青年機会がメンタルヘルス（他のすべての能力を可能にする）、次に労働力準備度、次に起業家能力に最も依存するという研究を反映しています。",
  q4Step3Title: "ステップ3：114のJCI国に適用",
  q4Step3: "YFIフレームワークはJCIが活動する114カ国すべてについて計算され、JCIの地理的フットプリントに直接整合する青年機会のグローバルマップを作成しました。",
  q4Step4Title: "ステップ4：結果を検証",
  q4Step4: "結果は2025年調査からのJCIメンバーフィードバックと青年開発に関する公開されたグローバル研究に対してクロスチェックされました。",
  q4Result: "結果：推測やモデルではなく、確立された研究に基づいた厳密で監査可能な指数。",

  // YFI Formula
  yfiFormulaTitle: "YFI計算式",
  yfiFormula: "YFI = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
  yfiMH: "MH = メンタルヘルス要素（40%の重み）",
  yfiWF: "WF = 労働力と教育要素（30%の重み）",
  yfiENT: "ENT = 起業家精神要素（30%の重み）",

  // Q5: Dashcity Methodology
  q5Title: "ダッシュシティ5フェーズ手法はどのように機能するか？",
  q5Answer: "ダッシュシティ5フェーズ手法は、観測所がどのように構築され、どのように進化するかの戦略的構造を提供します。各フェーズには特定の目的があります：",
  
  phase1: "特定",
  phase1Status: "完了",
  phase1Dates: "2025年9月-11月",
  phase1Objective: "完全な青年リーダーシップエコシステムをマッピング",
  phase1Activities: "JCI戦略計画2023-2027を分析；機関的ランドスケープを診断（114カ国、4,641地域組織、148,122メンバー）；ギャップを特定：JCIデータは統一された分析なしにサイロに存在",
  phase1Deliverable: "戦略的フレームワークと研究範囲",

  phase2: "測定",
  phase2Status: "完了",
  phase2Dates: "2025年10月-12月",
  phase2Objective: "厳密なデータ基準と検証プロセスを確立",
  phase2Activities: "7つの譲れない品質基準を確立；すべての一次データソースを検証（JCIインパクトレポート、調査、ビジョン2040）；グローバルデータソースを統合（WHO、ILO、UNESCO、世界銀行、GEM）；5ステップ検証プロセスを実装",
  phase2Deliverable: "データアーキテクチャと品質基準",

  phase3: "推進",
  phase3Status: "進行中",
  phase3Dates: "2025年12月-2026年1月",
  phase3Objective: "価値を伝え、ステークホルダーを関与させる",
  phase3Activities: "ステークホルダーエンゲージメントチャネルを開発；貢献ガイドラインとインセンティブ構造を構築；多言語リソースを作成（EN、PT、ES、FR、JA）",
  phase3Deliverable: "コミュニケーションツールキットとエンゲージメントインフラ",

  phase4: "指導",
  phase4Status: "進行中",
  phase4Dates: "2026年1月-2月",
  phase4Objective: "ガバナンスと品質監督を確立",
  phase4Activities: "検証プロセスを文書化（7ステップ品質ゲート）；更新サイクルを定義（年次、中間、継続的）；フィードバックループを確立（コミュニティ→チーム→協議会→理事会）",
  phase4Deliverable: "ガバナンスフレームワークと運営プロセス",

  phase5: "拡大",
  phase5Status: "計画中",
  phase5Dates: "2026年1月-2027年12月以降",
  phase5Objective: "リーチ、インパクト、グローバルな影響力を拡大",
  phase5Activities: "異なる対象者向けのコミュニケーション資料を作成；15以上の国内組織が積極的に貢献するまで拡大；1,000人以上の登録ユーザーに成長；学術研究者向け公開APIを開始",
  phase5Deliverable: "青年リーダーシップ開発のグローバルオーソリティ",

  phaseWhyTitle: "このアプローチが機能する理由",
  phaseWhy: "各フェーズは前のフェーズの上に構築されます。特定は何を構築するかを確立します。測定は厳密さを確保します。推進はステークホルダーを関与させます。指導はガバナンスを確保します。拡大は持続可能な成長を確保します。",

  // Q6: Areas of Opportunity
  q6Title: "JCIの4つの機会領域とは？",
  areaIndividual: "個人開発",
  areaIndividualDesc: "若者はトレーニング、メンタリング、実践経験を通じて、不可欠なリーダーシップスキル、自信、個人の成長を発展させます。",
  areaIndividualExamples: "例：パブリックスピーキング、ディベート、リーダーシップコース、スキル認定",
  
  areaCommunity: "コミュニティインパクト",
  areaCommunityDesc: "若いリーダーはコミュニティのニーズを特定し、社会に利益をもたらすソリューションを設計します。",
  areaCommunityExamples: "例：健康イニシアチブ、教育プログラム、災害救援、環境プロジェクト",
  
  areaInternational: "国際協力",
  areaInternationalDesc: "若いリーダーはグローバルネットワークを構築し、多様な視点を理解し、国境を越えて協力します。",
  areaInternationalExamples: "例：JCIツイニングパートナーシップ、TOYP賞、国際イベント、人権擁護",
  
  areaBusiness: "ビジネスと起業家精神",
  areaBusinessDesc: "若いリーダーはビジネスを立ち上げ、イノベーションを推進し、経済的機会を構築します。",
  areaBusinessExamples: "例：クリエイティブヤングアントレプレナー、JCIインビジネス、スタートアップアクセラレーター",

  // Q7: Connection between Areas and Observatory
  q7Title: "4つの領域と観測所の関係は？",
  q7Answer: "観測所はこれらの4つの領域が活動する外部コンテキストを測定します。",
  q7Detail: "YFIは追跡します：メンタルヘルス（すべての4つの領域に影響 — 健康な心はより良く学び、より良く構築する）、労働力と教育（個人開発+ビジネス領域をサポート）、起業家精神（ビジネス領域をサポート）。",
  q7Example: "観測所がアフリカ・中東のYFIスコアが53（ヨーロッパの75に対して）であることを示すとき、それは「これらの地域の若者はメンタルヘルス、雇用、ビジネス機会のギャップに直面しています。ここがJCIの4つの機会領域が最大のインパクトを持つ場所です」と言っています。",
  q7Summary: "4つの領域はJCIがリーダーを育成する方法です。観測所はそれがどこで、なぜ最も重要かを示します。",

  // Q8: Data-Driven Innovation
  q8Title: "データ駆動型イノベーション（第4戦略目標）とは？",
  q8Answer: "第4戦略目標は重要な質問に対処します：リーダーシップ開発が実際に機能しているかどうかをどのように知るか？どのように改善するか？どこに焦点を当てるべきか？",
  q8Means: "データ駆動型イノベーションとは：",
  q8Means1: "重要なことを測定する — 青年機会、JCI会員、インパクトに関する厳密なデータを使用",
  q8Means2: "インサイトのために分析する — パターンを見つける（例：「メンタルヘルスアクセスは地域間で22ポイント異なる」）",
  q8Means3: "情報に基づいた決定を下す — エビデンスに基づいてリソースを配分",
  q8Means4: "継続的な改善 — フィードバックを収集し、戦略を調整し、結果を測定",
  q8Conclusion: "観測所はJCIを「違いを生み出していると感じる」組織から「検証済みの監査可能なデータを使用してインパクトを証明し測定できる」組織へと変革します。",

  // Section 3: Data and Sources
  dataSourcesTitle: "データとソース",

  // Q9: What data sources?
  q9Title: "観測所はどのようなデータソースを使用しているか？",
  primaryDataTitle: "一次JCIデータ",
  primaryData1: "JCIインパクトレポート2025 — 147,670メンバー、4,641地域組織、年間1,000以上のプロジェクト、2,100万人以上の受益者",
  primaryData2: "JCIビジョン2040 — 戦略ロードマップ、成長目標、テクノロジーイノベーション計画",
  primaryData3: "2025年会員経験調査 — 4地域すべてで3,767人の回答者",
  
  secondaryDataTitle: "二次グローバルデータ",
  secondaryData1: "WHO精神保健アトラス2024 — 精神保健サービスカバレッジ、有病率推定",
  secondaryData2: "WHO死亡率データベース — 国別自殺死亡率",
  secondaryData3: "Sapien Labs世界の精神状態2024 — 主観的ウェルビーイング研究",
  secondaryData4: "ILOグローバル雇用動向2024-25 — 若年失業率、NEET統計",
  secondaryData5: "UNESCO統計研究所 — 教育登録、識字率",
  secondaryData6: "世界銀行 — 人的資本指数、ビジネスのしやすさ",
  secondaryData7: "GEMグローバル起業家精神モニター2024-25 — 起業家活動",
  secondaryData8: "ITUデジタル開発ダッシュボード — デジタルインフラとアクセス",
  
  dataTransparency: "すべてのソースは公開され検証されています。観測所には専有、機密、または制限付きデータはゼロです。",

  // Q10: Data quality
  q10Title: "データ品質はどのように確保されているか？",
  q10Answer: "7つの譲れない品質基準を実装しました：",
  qualityCriteria1: "検証可能 — 明確なソース識別（例：「WHO精神保健アトラス2024、12ページ」）",
  qualityCriteria2: "監査可能 — 完全な手法が文書化",
  qualityCriteria3: "追跡可能 — 特定された起源、匿名性なし",
  qualityCriteria4: "正確 — 提示前に検証済み",
  qualityCriteria5: "最新 — 明確な日付と時間的関連性",
  qualityCriteria6: "コンテキスト化 — 制限が明確に説明",
  qualityCriteria7: "引用 — ソースへの完全なクレジット",

  validationProcessTitle: "5ステップ検証プロセス",
  validationStep1: "提出（1-2日）",
  validationStep1Desc: "データ取り込みと予備的な完全性チェック",
  validationStep2: "検証（5-7日）",
  validationStep2Desc: "手法と計算の技術的検証",
  validationStep3: "監査（5-7日）",
  validationStep3Desc: "データ精度の独立監査",
  validationStep4: "ピアレビュー（5-7日）",
  validationStep4Desc: "主題専門家によるレビュー",
  validationStep5: "公開（2-3日）",
  validationStep5Desc: "最終認証と透明性ドキュメント",

  // Q11: Zero synthetic data
  q11Title: "「合成データゼロ」とはどういう意味か？",
  q11Answer: "合成データゼロとは：表示されるすべての数字は実際のデータソースに追跡できます。数字は発明、推定、またはモデルによって生成されていません。",
  
  notIncludedTitle: "含まれていないもの",
  notIncluded1: "現在の事実として提示された予測",
  notIncluded2: "AI生成の統計",
  notIncluded3: "データギャップを埋める補間値",
  notIncluded4: "モデルベースの推定（明示的にラベル付けされていない限り）",
  notIncluded5: "仮定された分布",
  
  includedTitle: "含まれているもの",
  included1: "実際のカウント（例：「147,670メンバー」）",
  included2: "調査ベースの統計（例：「3,767人の回答者」）",
  included3: "公開された指数（例：「YFIスコア：75.28」）",
  included4: "明示的にマークされたシナリオ（例：「2040年ビジョン：500,000メンバー」— 戦略目標として明確にラベル付け）",
  
  zeroSyntheticWhy: "これが重要な理由：これらの数字を信頼できます。アルゴリズムによって生成されたり、コンサルタントによって推定されたりしていません。監査可能なソースからの検証済みの事実です。",

  // Section 4: Using the Observatory
  usingTitle: "観測所の活用",

  // Q12: Update cycles
  q12Title: "データはどのくらいの頻度で更新されるか？",
  annualUpdatesTitle: "年次更新（包括的）",
  annualUpdates1: "11月-12月：すべてのソースからのデータ収集",
  annualUpdates2: "1月-2月：検証と比較分析",
  annualUpdates3: "3月：新版公開",
  annualUpdates4: "4月-5月：ステークホルダーフィードバックとエンゲージメント",
  
  intermediateUpdatesTitle: "中間更新（必要に応じて）",
  intermediateUpdates1: "新しい重要な研究結果",
  intermediateUpdates2: "主要な組織変更またはプログラムイノベーション",
  intermediateUpdates3: "重要な政策開発",
  intermediateUpdates4: "エラー修正または明確化",
  
  continuousUpdatesTitle: "継続的更新（進行中）",
  continuousUpdates1: "リンク更新と参照修正",
  continuousUpdates2: "プラットフォーム技術改善",
  continuousUpdates3: "ユーザーインターフェース強化",
  continuousUpdates4: "アクセシビリティ改善",
  
  updateGoal: "目標は、ユーザーを絶え間ないマイクロ更新で圧倒することなく、観測所を最新かつ正確に保つことです。",

  // Q13: Using the data
  q13Title: "このデータを研究や組織に使用できるか？",
  q13Answer: "はい、もちろんです。観測所は以下のために設計されています：",
  
  useNationalTitle: "国内JCI組織",
  useNational1: "グローバルデータに対して進捗をベンチマーク",
  useNational2: "地域リーダーシップの取り組みが最大のインパクトを持つ場所を特定",
  useNational3: "拡大場所についてデータに裏付けられた戦略的決定を下す",
  
  useLocalTitle: "地域JCI組織",
  useLocal1: "あなたの都市/地域の青年機会ランドスケープを理解",
  useLocal2: "ローカルプロジェクトをグローバルな機会課題に接続",
  useLocal3: "あなたの活動が優先ギャップに対処しているかを追跡",
  
  useAcademicTitle: "学術研究者",
  useAcademic1: "検証済みデータと公開された手法へのアクセス",
  useAcademic2: "プログラムアクセス用API（2026年予定）",
  useAcademic3: "観測所の調査結果を引用し発展させる許可",
  
  usePolicyTitle: "政策立案者と政府",
  usePolicy1: "あなたの国の青年機会に関するエビデンス",
  usePolicy2: "近隣地域との比較",
  usePolicy3: "教育、雇用、メンタルヘルス政策のための戦略的インテリジェンス",
  
  useCorporateTitle: "企業パートナー",
  useCorporate1: "あなたの市場における青年開発ニーズを理解",
  useCorporate2: "JCIとのパートナーシップが最もインパクトのある場所を特定",
  useCorporate3: "企業責任報告のための検証済みデータにアクセス",
  
  publicAPIs: "公開API — 研究者と開発者向けに2026年第2四半期開始",

  // Q14: Reproducibility
  q14Title: "研究者は手法を再現できるか？",
  q14Answer: "はい、完全に。すべてのコンポーネントは文書化され、公開されています：",
  q14Formula: "YFI計算式（公開）：YFI = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
  q14Sources: "ソースデータ（すべて公開）：WHO精神保健アトラス、ILO ILOSTATデータベース、UNESCO統計、世界銀行指標、GEMグローバル起業家精神モニター、JCIインパクトレポート2025",
  q14Methodology: "完全な手法ドキュメント — 手法ページで完全に公開",
  q14Commitment: "同じソースにアクセスできる研究者は誰でも分析を複製し、計算を検証し、結論をテストできます。それが透明性へのコミットメントです。",

  // Section 5: Future Development
  futureTitle: "将来の開発",

  // Q15: Future roadmap
  q15Title: "観測所の将来は？",
  q15Answer: "観測所は段階的な拡大計画を通じて進化しています：",
  
  roadmap2026Title: "2026年（ベータ — 拡大）",
  roadmap2026Items: "15以上の国内組織を積極的な貢献者として拡大；ユーザーコミュニティを200人以上に成長；学術研究者向け公開APIを開始；高度な分析ダッシュボードを開発；政策影響のドキュメント化を開始",
  roadmap2026Target: "目標：200人以上のユーザー、年間10以上のメディア記事が観測所を引用",
  
  roadmap2027Title: "2027年以降（拡大 — グローバルオーソリティ）",
  roadmap2027Items: "青年リーダーシップ開発のグローバルリファレンスとしての地位を確立；国連（ECOSOC）、WEF、UNESCOプラットフォームとの統合；10以上の国で目に見える政策影響；1,000人以上のエンゲージドユーザーのグローバルコミュニティ；トップメディアアウトレットで認知される年次出版物",
  roadmap2027Target: "目標：年間500以上のメディア記事、10以上の国の政策への影響",
  
  innovationsTitle: "計画された戦略的イノベーション",
  innovation2026: "2026年：メンターディレクトリ、パートナーシップページ、リアルタイムアドボカシーカウンター",
  innovation2027: "2027年以降：月刊研究ニュースレター、専門家ウェビナーシリーズ、インタラクティブスキル評価ツール、助成金マッチング計算機",

  // Q16: Contributing
  q16Title: "観測所にどのように貢献できるか？",
  
  contributeNationalTitle: "国内組織が貢献できること",
  contributeNational: "地域分析とインサイト；成功プログラムのケーススタディ；国内イベントとプログラムからのデータ；調査結果がコンテキストにどのように適用されるかのフィードバック",
  
  contributeLocalTitle: "地域組織が貢献できること",
  contributeLocal: "プロジェクト成果とインパクト指標；メンバー開発ストーリー；コミュニティリーチと受益者に関するデータ；ローカルイニシアチブからの教訓",
  
  contributeMemberTitle: "個人メンバーが貢献できること",
  contributeMember: "JCIプログラムを通じて学んだスキルに関するフィードバック；JCIがリーダーシップの軌跡をどのように変えたかのストーリー；雇用結果に関するデータ；改善の提案",
  
  contributeHowTitle: "貢献方法",
  contributeHow1: "貢献ページを訪問",
  contributeHow2: "貢献ガイドラインをレビュー",
  contributeHow3: "必要なドキュメントと共に貢献フォームを完成",
  contributeHow4: "5-7日以内にフィードバックを受け取る",
  contributeHow5: "公開時に貢献者として認識される",
  
  contributeIncentivesTitle: "貢献者へのインセンティブ",
  contributeIncentive1: "すべての観測所出版物での正式な引用",
  contributeIncentive2: "デジタルコラボレーション証明書",
  contributeIncentive3: "ソーシャルメディアでの認識",
  contributeIncentive4: "学術CVに有効",
  contributeIncentive5: "一般公開前の観測所インサイトへのアクセス",

  // Q17: Vision 2040 alignment
  q17Title: "JCIビジョン2040とどのように整合しているか？",
  q17Goal: "ビジョン2040の目標：2040年までに500,000人の若いリーダーをエンパワー",
  q17Contribution: "観測所の貢献：この目標を達成するための戦略的インテリジェンス",
  
  vision2040PillarTitle: "ビジョン2040の柱",
  vision2040RoleTitle: "観測所の役割",
  
  vision2040Pillar1: "リーダーシップ開発の卓越性",
  vision2040Role1: "スキルギャップをマッピング（コミュニケーション#1、クリティカルシンキング#2）；トレーニングの優先事項を特定",
  
  vision2040Pillar2: "テクノロジーとデジタルイノベーション",
  vision2040Role2: "デジタル採用を追跡（jci.cc：13,351ユーザー、260Kソーシャルフォロワー）；テックロードマップを導く",
  
  vision2040Pillar3: "公平性とインクルージョン",
  vision2040Role3: "地域的な機会不平等を明らかに（22ポイントのYFIギャップ）；インパクトが最も必要な場所を特定",
  
  vision2040Pillar4: "持続可能性と気候",
  vision2040Role4: "環境政策整合のための青年機会コンテキストを提供",
  
  vision2040QuestionsTitle: "観測所が答える戦略的質問",
  vision2040Q1: "どこで拡大するか？ — アフリカ・中東（最低YFI = インパクトの最大機会）",
  vision2040Q2: "何を優先するか？ — メンタルヘルス（40%のYFI重み = 最大の差別化要因）",
  vision2040Q3: "どのようにスケールするか？ — テクノロジー（2040年までに80%以上の採用目標）",
  vision2040Q4: "成功の測定？ — グローバルYFIを4-7ポイント改善；アフリカ・中東のギャップを50%縮小",
  
  vision2040Summary: "観測所はビジョン2040を願望からエビデンスベースの戦略へと変革します。",

  // Section 6: 2026 Plan of Action (EVOLVE)
  evolveTitle: "2026年行動計画：EVOLVE",

  // Q21: EVOLVE connection
  q21Title: "観測所は2026年行動計画とどのように関連しているか？",
  q21Answer: "2026年行動計画は3つのコア原則を確立しています：LEAD | CONNECT | EVOLVE。観測所はEVOLVE原則の直接的な表現です。",
  q21Quote: "「私たちは大義にふさわしい焦点でLEADし、創造したい変化とCONNECTし、急速に変化する世界とともにEVOLVEする必要があります。」",
  
  evolveHow: "観測所がEVOLVEを可能にする方法：",
  
  evolve1Title: "データ駆動型意思決定を通じた進化",
  evolve1: "観測所はグローバルに最も必要とされるスキル、リーダーシップ開発が最大のインパクトを持つ場所、青年機会指標を通じて成功を測定する方法を示すエビデンスを提供します。",
  evolve1Summary: "データがなければ、戦略的に進化することはできません — 反応するだけです。観測所はエビデンスに基づいた積極的な進化を可能にします。",
  
  evolve2Title: "4つの行動の柱のサポート",
  actionPillar1: "サステナビリティファースト",
  actionPillar1Desc: "メンタルヘルス（40%のYFI重み）と長期的な青年機会を追跡し、活動が持続可能な未来を創造することを確保",
  actionPillar2: "グローバルリーダーシップの構築",
  actionPillar2Desc: "リーダーシップ開発が最も必要な場所をマッピング；JCIプログラムが若者が優先するスキルを開発することを検証",
  actionPillar3: "世界中でのビジネス",
  actionPillar3Desc: "起業家エコシステムデータ（30%のYFI重み）を提供；JCIビジネスプログラムが最大のインパクトを持つ場所を特定",
  actionPillar4: "生活と文化の接続",
  actionPillar4Desc: "青年機会の地域差（22ポイントのYFIギャップ）を示し、なぜ異文化接続が重要かを実証",
  
  evolve3Title: "重要なKPIの測定",
  evolve3: "観測所はKPIを外部の青年機会データに接続することで意味のあるものにします。例えば、アフリカ・中東の会員が30%成長した場合、観測所はこれが重要であることを示します。なぜならその地域は最も低いYFI（53）と最大の機会ギャップを持っているからです。",
  
  evolve4Title: "「基本に戻る」と戦略的イノベーションの出会い",
  evolve4: "観測所は2026年計画のフェーズ1「基本に戻る」を補完し、基礎プログラムに戦略的コンテキストを提供し、トレーナー認定のためのベンチマークデータを提供し、メンター-メンティー2.0をエビデンスでサポートします。",
  
  evolve5Title: "目的を持ったグローバルイベント",
  evolve5: "観測所はグローバルイベント（ユースリーダーシップサミット、サステナビリティウィーク、女性リーダーシップフォーラム）をセレモニー的ではなくエビデンスベースにするデータを提供します。",
  
  evolveKeyConnection: "重要な接続：EVOLVEは急速に変化する世界に適応することを意味します。観測所はJCIがどのように進化するかを示します — 直感ベースからデータ駆動型リーダーシップへ、「違いを生み出していると思う」から「インパクトを証明できる」へ。",
  
  evolveSummary: "2026年行動計画は方向性を設定します（LEAD | CONNECT | EVOLVE）。観測所は進化を戦略的、測定可能、監査可能にするインテリジェンスを提供します。",

  // Section 7: Common Questions
  commonTitle: "よくある質問",

  // Q22: Privacy
  q22Title: "観測所はプライバシーと機密性にどのように対処しているか？",
  
  privacyMemberTitle: "メンバープライバシー",
  privacyMember1: "個人メンバーデータは公開されません",
  privacyMember2: "すべての調査データは集約され匿名化されます",
  privacyMember3: "回答者は結果で特定できません",
  privacyMember4: "個人情報はプライバシーポリシーに従って保護されます",
  
  privacyOrgTitle: "組織データ",
  privacyOrg: "集約データのみ公開（例：「147,670メンバー」）",
  
  privacySecurityTitle: "データセキュリティ",
  privacySecurity1: "暗号化された送信と保存",
  privacySecurity2: "定期的なセキュリティ監査",
  privacySecurity3: "生データへの限定アクセス（運営チームのみ）",
  privacySecurity4: "GDPRおよび地域プライバシー法への準拠",

  // Q23: Disagreement
  q23Title: "観測所の調査結果に同意できない場合は？",
  q23Answer: "あなたのフィードバックは不可欠です。異議は改善に役立ちます。",
  q23Process: "方法は以下の通りです：",
  q23Step1: "詳細なフィードバックをtmariano@jci.ccに送信",
  q23Step2: "懸念を明確に（手法、データ精度、解釈）",
  q23Step3: "フィードバックはレビューのために関連する開発協議会に送られます",
  q23Step4: "2週間以内に、手法を説明するか、あなたの有効なポイントを認めます",
  q23Step5: "フィードバックがエラーを明らかにした場合、修正してあなたを引用します",
  q23Summary: "観測所は建設的な異議を通じて進化します。私たちは正しくあろうとしているのではなく — 正確であろうとしています。",

  // Q24: Staying updated
  q24Title: "観測所の開発について最新情報を得るには？",
  
  newsletterTitle: "ニュースレター（2026年第2四半期開始）",
  newsletter1: "新しい調査結果の月間ハイライト",
  newsletter2: "貢献者スポットライト",
  newsletter3: "今後のイノベーション",
  newsletter4: "認証ページで購読",
  
  webinarsTitle: "ウェビナー（2026年第3四半期開始）",
  webinars1: "観測所の次元の専門家による説明",
  webinars2: "戦略計画に観測所データを使用する方法",
  webinars3: "開発協議会とのQ&A",
  webinars4: "無料、すべてに開放、多言語",
  
  directEmailTitle: "ダイレクトメール",
  directEmail: "tmariano@jci.cc — 一般的なお問い合わせ、特定のデータや手法に関する質問",

  // Contact Section
  contactTitle: "お問い合わせとリソース",
  
  contactTechnicalTitle: "技術的な質問",
  contactTechnical: "tmariano@jci.cc",
  
  contactRegionalTitle: "地域に関する質問",
  contactAmericas: "アメリカ：adc@jci.cc",
  contactEurope: "ヨーロッパ：edc@jci.cc",
  contactAfrica: "アフリカ・中東：amdec@jci.cc",
  contactAsia: "アジア太平洋：apdc@jci.cc",
  
  keyResourcesTitle: "主要リソース",
  keyResource1: "完全な手法",
  keyResource2: "データダッシュボード",
  keyResource3: "貢献方法",
  
  accessibilityTitle: "アクセシビリティ",
  accessibility1: "英語、ポルトガル語、スペイン語、フランス語、日本語で利用可能",
  accessibility2: "すべてのコンテンツのダウンロード可能なPDF（近日公開）",
  accessibility3: "スクリーンリーダー対応",
  accessibility4: "リクエストに応じて追加言語",

  // CTA
  ctaTitle: "探索する準備はできましたか？",
  ctaDescription: "青年未来観測所はエビデンスに基づいて構築されています。私たちのコミットメントは、変化する世界のためにリーダーを育成するJCIの使命をサポートするための透明性、厳密さ、継続的な改善です。",
  ctaExplore: "観測所を探索",
  ctaContribute: "観測所に貢献",
  ctaContact: "お問い合わせ",

  // Source
  sourceAttribution: "ソース：JCI戦略計画2023-2027、ダッシュシティ手法、JCIインパクトレポート2025",
};